// React
import { useContext } from "react"

// Components
import { BaseTable, Card, Checkbox, DateInput, HeaderMenu, Input, Select, TableHeader, TableSection, Textarea } from "components"

// Contexts
import { LocationContext, LocationProvider } from "contexts"

// Icons
import { TbChevronRight } from "react-icons/tb"

// Third-Party Libraries
import moment from "moment"

// Types
import type { DataType, DetailType } from "../types"

// Utils
import { convertNumber, useProduct } from "utils"
import { default_option } from "pages/Purchase/utils"
import { useSupplier } from "../../components/Supplier/utils"
import { billStatusList } from "../../EnterBill/utils"

export function InputSection(params: {
  data: DataType
}): JSX.Element {
  // Vars
  const { datareject, detailreject } = params.data
  const sum: {
    subtotal: number
    tax: number
  } = detailreject.reduce(
    (acc, item) => {
      return {
        subtotal: acc.subtotal + item.total,
        tax: acc.tax + (item.total * item.tax_rate) / 100
      }
    },
    {
      subtotal: 0,
      tax: 0
    }
  )

  return (
    <>
      <HeaderMenu title="DATA ENTRY | REJECT RECORD">
        {datareject.trx_code}
      </HeaderMenu>

      <section className="grid lg:grid-cols-3 gap-x-6 gap-y-3 items-end">
        <Supplier vendor_id={datareject.vendor_id} />

        <section className="lg:justify-self-end mb-3">
          <Checkbox readOnly value>
            TAX-INCLUSIVE
          </Checkbox>
        </section>
      </section>

      <Card>
        <Card.Body className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
          <section className="flex flex-col gap-3">
            <section>
              <LocationProvider>
                <Deliver location_id={datareject.location_id} />
              </LocationProvider>

              <Textarea disabled value={datareject.address} />
            </section>

            <Input
              disabled
              label="MEMO"
              value={datareject.memo}
            />

            <Input
              disabled
              label="LINK TO BILL ID"
              value={datareject.trx_bill_code}
            />
          </section>

          <section className="flex flex-col gap-3">
            <DateInput
              disabled
              label="DATE"
              selected={moment(datareject.transaction_date).toDate()}
              onChange={() => {}}
            />

            <Input
              disabled
              label="REFERENCE"
              value={datareject.referensi}
            />
          </section>
        </Card.Body>
      </Card>

      <BaseTable>
        <TableHeader>
          <tr>
            <th>QTY REJECTED</th>
            <th>QTY RECEIVED</th>
            <th>BACK REJECTED</th>
            <th>UNIT</th>
            <th>ITEM CODE</th>
            <th>DESCRIPTION</th>
            <th>PRICE</th>
            <th>DISC (%)</th>
            <th>TOTAL</th>
            <th>TAX</th>
          </tr>
        </TableHeader>

        <tbody>
          <BodyTable items={detailreject} />
        </tbody>
      </BaseTable>

      <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
        <section className="flex flex-col gap-3">
          <TableSection>
            <TableSection.Row
              label="REJECT STATUS"
              value={billStatusList[datareject.reject_status.toString()]}
            />

            <TableSection.Row
              label="APPROVE STATUS"
              value={default_option.find((item) => item.value === datareject.approve_status.toString())?.label ?? "-"}
            />

            <TableSection.Row
              label="APPROVED BY"
              value={datareject.approve_by}
            />
          </TableSection>
        </section>

        <section className="flex flex-col lg:items-end gap-3">
          <BaseTable
            plain
            className="w-full min-w-[100px] md:max-w-[400px] text-right">
            <tbody>
              <tr>
                <td className="w-[1px] whitespace-nowrap">SUBTOTAL</td>
                <td>{convertNumber(sum.subtotal).intoCurrency}</td>
              </tr>

              <tr>
                <td className="w-[1px] whitespace-nowrap">TAX</td>
                <td>{convertNumber(sum.tax).intoCurrency}</td>
              </tr>

              <tr className="font-bold">
                <td className="w-[1px] whitespace-nowrap">TOTAL REJECTED</td>
                <td>{convertNumber(sum.subtotal + sum.tax).intoCurrency}</td>
              </tr>
            </tbody>
          </BaseTable>
        </section>
      </section>
    </>
  )
}

function BodyTable(params: { items: DetailType[] }): JSX.Element {
  // Vars
  const items = params.items

  // Hooks
  const { data } = useProduct()

  if (!items.length) {
    return (
      <tr>
        <td colSpan={10} />
      </tr>
    )
  }

  return (
    <>
      {items.map((item, key) => {
        // Vars
        const product = data.find((i) => i.product_id === item.product_id)

        return (
          <tr key={key}>
            <td>{item.quantity_reject}</td>
            <td>{item.qty_received_item}</td>
            <td>{item.qty_back_delivered}</td>
            <td>{item.unit_measure}</td>
            <td>{product?.product_barcode}</td>
            <td>{product?.product_name}</td>
            <td>{convertNumber(item.price_reject).intoCurrency}</td>
            <td>{`${convertNumber(item.discount).intoCurrency}%`}</td>
            <td>{convertNumber(item.total).intoCurrency}</td>
            <td>
              {item.tax_name} ({item.tax_rate}%)
            </td>
          </tr>
        )
      })}
    </>
  )
}

function Deliver(params: { location_id: number }): JSX.Element {
  // Hooks
  const { data, isLoading } = useContext(LocationContext)

  return (
    <Select
      isDisabled
      label="DELIVER TO"
      placeholder="Select Deliver"
      isLoading={isLoading}
      options={data}
      value={data.find((item) => item.value === params.location_id.toString()) ?? null}
    />
  )
}

function Supplier(params: { vendor_id: number }): JSX.Element {
  // Hooks
  const { data, isLoading } = useSupplier()

  // Vars
  const selectedItem = data.find(
    (item) => item.value === params.vendor_id.toString()
  )

  return (
    <>
      <Select
        isDisabled
        label="SUPPLIER"
        placeholder="Select Supplier"
        isLoading={isLoading}
        options={data}
        value={selectedItem ?? null}
      />

      <div className="mb-3 flex flex-wrap">
        TRADING TERM{" "}
        <span className="mt-1">
          <TbChevronRight />
        </span>{" "}
        Net {selectedItem?.payment_term_vendor_name} days after OEM
      </div>
    </>
  )
}